<script setup lang="ts">
/** Subscription / Application for Shares — allottee 1명당 1문서.
 *  type(individual/corporate)은 allottee 데이터(category/ID)로 자동판정되며 문구·라벨을 분기한다.
 *  · 값 필드는 :index 로 해당 allottee 저장키에 바인딩 (subStoreKey).
 *  · KYC 플래그/서명자는 allottee 별로 분리(index 접미사). has_constitution 만 회사 공통.
 *  create-task 시 individual→TMP081, corporate→TMP082 로 문서가 생성된다. */
import { computed, inject } from 'vue';
import { OTC_DOC_CTX } from './otcContext';
import OtcDocVarField from './OtcDocVarField.vue';
import OtcSigners from './parts/OtcSigners.vue';

const props = defineProps<{ index: number; type: 'individual' | 'corporate' }>();
const ctx = inject(OTC_DOC_CTX)!;

const isCorp = computed(() => props.type === 'corporate');
const hasConstitution = computed(() => ctx.flag('has_constitution'));
// allottee 별 플래그/서명자 키
const fk = (name: string) => `${name}_${props.index}`;
const signerKey = computed(() => `subscription_${props.index}`);
const flag = (name: string) => ctx.flag(fk(name));
const toggle = (name: string) => ctx.toggleFlag(fk(name));
</script>

<template>
  <div class="allot-doc">
    <div class="doc-head">
      <div class="doc-company">APPLICATION / SUBSCRIPTION FOR SHARES</div>
      <div class="doc-meta">({{ isCorp ? 'Corporate' : 'Individual' }} Subscriber)</div>
    </div>

    <p class="doc-p"><b>Date:</b> <OtcDocVarField field-key="letter_date" :index="index" /></p>
    <p class="doc-p">To the Board of Directors of <OtcDocVarField field-key="company_name" :index="index" /></p>
    <p class="doc-p">Registered Office: <OtcDocVarField field-key="company_address" :index="index" /></p>
    <p class="doc-p">
      {{ isCorp ? 'We,' : 'I,' }} <OtcDocVarField field-key="subscriber_name" :index="index" />
      [{{ isCorp ? 'Registration No.' : 'NRIC / Passport No.' }} <OtcDocVarField field-key="subscriber_id" :index="index" />], of
      <OtcDocVarField field-key="subscriber_address" :index="index" />, hereby apply to subscribe for
      <OtcDocVarField field-key="shares_subscribed" :index="index" /> ordinary shares of RM<OtcDocVarField field-key="share_price" :index="index" />
      each in the Company, and request that the said shares be allotted to {{ isCorp ? 'us' : 'me' }} in accordance with the
      <span class="doc-toggle" title="Constitution / Articles of Association" @click="ctx.toggleFlag('has_constitution')">{{ hasConstitution ? 'Constitution' : 'Articles of Association' }}</span>
      of the Company.
    </p>

    <p class="doc-lbl-title">Subscriber Particulars</p>
    <table class="cdd-tbl">
      <tbody>
        <tr><td class="cdd-lcell">Subscriber Type</td><td><b>{{ isCorp ? 'Corporate' : 'Individual' }}</b> <small style="color:#5a6b8c">(auto — from allottee data)</small></td></tr>
        <tr><td class="cdd-lcell">Occupation / Nature of Business</td><td><OtcDocVarField field-key="subscriber_occupation" :index="index" /></td></tr>
        <tr><td class="cdd-lcell">{{ isCorp ? 'Date of Incorporation' : 'Date of Birth' }}</td><td><OtcDocVarField field-key="subscriber_dob" :index="index" /></td></tr>
        <tr><td class="cdd-lcell">{{ isCorp ? 'Nationality / Place of Incorporation' : 'Nationality' }}</td><td><OtcDocVarField field-key="subscriber_nationality" :index="index" /></td></tr>
        <tr><td class="cdd-lcell">Race</td><td><OtcDocVarField field-key="subscriber_race" :index="index" /></td></tr>
      </tbody>
    </table>

    <p class="doc-lbl-title">KYC / PEP Declaration</p>
    <p class="doc-p">
      <span class="doc-toggle" @click="toggle('is_politician')">{{ flag('is_politician') ? (isCorp ? '✓ a director / controller is a politician or holds public office' : '✓ I am a politician / hold public office') : (isCorp ? '☐ a director / controller is a politician or holds public office' : '☐ I am a politician / hold public office') }}</span>
      <template v-if="flag('is_politician')"> — <OtcDocVarField field-key="politician_details" :index="index" /></template>
    </p>
    <p class="doc-p">
      <span class="doc-toggle" @click="toggle('is_pep_connected')">{{ flag('is_pep_connected') ? (isCorp ? '✓ connected to a PEP' : '✓ I am connected to a PEP') : (isCorp ? '☐ connected to a PEP' : '☐ I am connected to a PEP') }}</span>
      <template v-if="flag('is_pep_connected')"> — <OtcDocVarField field-key="pep_connection_details" :index="index" /></template>
    </p>
    <p class="doc-p">
      <span class="doc-toggle" @click="toggle('holds_govt_position')">{{ flag('holds_govt_position') ? (isCorp ? '✓ holds a government position' : '✓ I hold a government position') : (isCorp ? '☐ holds a government position' : '☐ I hold a government position') }}</span>
      <template v-if="flag('holds_govt_position')"> — <OtcDocVarField field-key="govt_position_details" :index="index" /></template>
    </p>
    <p class="doc-p">
      <span class="doc-toggle" @click="toggle('is_trustee')">{{ flag('is_trustee') ? '✓ subscribing as trustee' : '☐ subscribing as trustee' }}</span>
    </p>

    <p class="doc-lbl-title">Beneficial Owner</p>
    <table class="cdd-tbl">
      <tbody>
        <tr><td class="cdd-lcell">Beneficial Owner Name</td><td><OtcDocVarField field-key="bo_full_name" :index="index" /></td></tr>
        <tr><td class="cdd-lcell">Nature of Control</td><td><OtcDocVarField field-key="bo_nature" :index="index" /></td></tr>
        <tr><td class="cdd-lcell">Place</td><td><OtcDocVarField field-key="bo_place" :index="index" /></td></tr>
        <tr><td class="cdd-lcell">Registration No.</td><td><OtcDocVarField field-key="bo_registration_no" :index="index" /></td></tr>
        <tr><td class="cdd-lcell">Registered Address</td><td><OtcDocVarField field-key="bo_registered_address" :index="index" /></td></tr>
        <tr><td class="cdd-lcell">Business Address</td><td><OtcDocVarField field-key="bo_business_address" :index="index" /></td></tr>
      </tbody>
    </table>

    <p class="doc-p" style="margin-top:12px">
      No. of Shares Offered: <OtcDocVarField field-key="shares_offered" :index="index" /> &nbsp;·&nbsp;
      No. of Shares Accepted: <OtcDocVarField field-key="shares_accepted" :index="index" />
    </p>

    <p class="doc-p"><span class="cdd-cbx sub-check" @click="toggle('confirm_accurate')"><span class="box">{{ flag('confirm_accurate') ? '☑' : '☐' }}</span>{{ isCorp ? 'We' : 'I' }} confirm that the information provided is accurate and complete.</span></p>
    <p class="doc-p"><span class="cdd-cbx sub-check" @click="toggle('confirm_clean')"><span class="box">{{ flag('confirm_clean') ? '☑' : '☐' }}</span>{{ isCorp ? 'We are' : 'I am' }} not a sanctioned person and the source of funds is legitimate.</span></p>
    <p class="doc-p"><span class="cdd-cbx sub-check" @click="toggle('confirm_consent')"><span class="box">{{ flag('confirm_consent') ? '☑' : '☐' }}</span>{{ isCorp ? 'We' : 'I' }} consent to the processing of the personal data provided herein.</span></p>

    <p class="doc-lbl-title">{{ isCorp ? 'Signed for and on behalf of the Corporate Subscriber' : 'Signed by the Subscriber' }}</p>
    <OtcSigners :var-key="signerKey" />
  </div>
</template>

<style scoped>
.sub-check { cursor: pointer; user-select: none; }
.sub-check .box { display: inline-block; min-width: 1.2em; }
.sub-check:hover .box { color: #1B84FF; }
</style>
